// Renderer de markdown leve para saídas de IA (avaliação no resultado +
// explicação por questão na revisão). Cobre o que o modelo costuma cuspir:
//   - ## headers
//   - **bold**, *italic*, `code`
//   - ==marker== (highlight)
//   - * item / - item / 1. item (listas, com aninhamento por indentação 2sp)
//   - quebras de linha
//   - $...$ / $$...$$ via KaTeX (auto-render no mount)
//
// Como NÃO é conversacional, o conteúdo entra como um veredito — sem
// sugestões interativas. O componente vem com um botão Copiar pra colar
// em plataformas externas.

import { useEffect, useRef, useState } from "preact/hooks";
import { Check, Copy } from "lucide-react";
import { ensureKaTeX, renderMathIn } from "./StudyWorkspace";
import { COLOR_TOKENS as UI_COLOR_TOKENS, normalizeColorName } from "@app/ui/lib/markdown";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Restaura math protegida com escape mínimo (só `&` e `<`). O KaTeX auto-render
// lê o `textContent` — que o browser já decodifica —, então `\frac{a}{b}`
// precisa chegar intacto. [[project-markdown-math-protect]]
function escapeMathForHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
}

// Mapa de cores semânticas — espelha o do `renderInline` em StudyWorkspace.tsx
// para que a IA possa usar os mesmos marcadores (`{{color:azul}}...{{/color}}`)
// que o conteúdo das questões usa. Mantido aqui em duplicata para evitar
// import circular entre aiMarkdown e StudyWorkspace.
const AI_COLOR_TOKENS: Record<string, string> = {
  azul: "#2563eb",
  vermelho: "#dc2626",
  verde: "#059669",
  amarelo: "#ca8a04",
  laranja: "#ea580c",
  roxo: "#7c3aed",
  rosa: "#db2777",
  cinza: "#6b7280"
};

function renderInlineForAi(raw: string): string {
  // Protege math INLINE (`\(...\)` e `$...$` numa linha) ANTES de escapar e
  // transformar — senão os regexes de italic/underscore/code mastigam o LaTeX
  // e o KaTeX não casa os delimitadores. Restaurado no fim com escape mínimo.
  // [[project-markdown-math-protect]]
  const inlineTokens: string[] = [];
  let pre = raw.replace(/\\\(([\s\S]+?)\\\)/g, (m) => {
    inlineTokens.push(m);
    return `INL${inlineTokens.length - 1}`;
  });
  pre = pre.replace(/(?<![\\$])\$([^\n$]+?)\$(?!\$)/g, (m) => {
    inlineTokens.push(m);
    return `INL${inlineTokens.length - 1}`;
  });
  let s = escapeHtml(pre);
  // Ordem: estendidos (==, ++, ~~) antes de bold/italic.
  s = s.replace(
    /`([^`\n]+)`/g,
    '<code class="rounded bg-accent px-1 py-0.5 text-[0.92em]">$1</code>'
  );
  // Shim de tolerância (compartilhado com apps/ui markdown + StudyWorkspace):
  // aceita qualquer cor de 2-24 chars; normalizeColorName mapeia sinônimos
  // PT-BR (lilás→roxo, ciano→azul, arroxeado→roxo). Cor desconhecida vira
  // <strong> pra preservar ênfase sem mostrar brackets quebrados.
  s = s.replace(
    /\[\s*([a-zA-ZÀ-ÿ_-]{2,24})\s*\]([\s\S]*?)\[\s*\/\s*([a-zA-ZÀ-ÿ_-]{2,24})\s*\]/g,
    (_full, openName: string, inner: string) => {
      const canonical = normalizeColorName(openName);
      if (canonical) return `{{color:${canonical}}}${inner}{{/color}}`;
      return `<strong>${inner}</strong>`;
    }
  );
  // {{color:nome}}texto{{/color}} — cores semânticas inline (fonte de verdade
  // pra cor → hex é UI_COLOR_TOKENS de @app/ui; AI_COLOR_TOKENS fica como
  // fallback se o nome não estiver na canônica, garantindo paleta antiga).
  s = s.replace(
    /\{\{color:([a-zA-ZÀ-ÿ_-]+)\}\}([\s\S]*?)\{\{\/color\}\}/gi,
    (_m, name: string, inner: string) => {
      const canonical = normalizeColorName(name) ?? name.toLowerCase();
      const c = UI_COLOR_TOKENS[canonical] ?? AI_COLOR_TOKENS[canonical];
      if (!c) return inner;
      return `<span style="color:${c};font-weight:600;">${inner}</span>`;
    }
  );
  s = s.replace(
    /==([^=\n]+)==/g,
    '<mark class="rounded-sm bg-amber-400/40 px-0.5 text-foreground dark:bg-amber-500/30">$1</mark>'
  );
  s = s.replace(/\+\+([^+\n]+)\+\+/g, "<u>$1</u>");
  s = s.replace(/~~([^~\n]+)~~/g, "<s>$1</s>");
  // ***bold+italic*** primeiro pra capturar a combinação tripla antes de
  // bold/italic individuais cortarem errado.
  s = s.replace(
    /\*\*\*([^*\n]+)\*\*\*/g,
    "<strong><em>$1</em></strong>"
  );
  // **bold** — agora tolera asteriscos solitários no meio (ex: italic
  // aninhado: `**X *Y* Z**`). Lookahead `\*(?!\*)` permite `*` único mas
  // não permite começar uma nova fronteira `**`. Não-greedy pra não engolir
  // múltiplos blocos em sequência.
  s = s.replace(
    /\*\*((?:[^*\n]|\*(?!\*))+?)\*\*/g,
    "<strong>$1</strong>"
  );
  s = s.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  s = s.replace(
    /(^|[\s>([{,;:!?])_([^_\n\\^]+)_(?=[\s<)\]}.,;:!?]|$)/g,
    "$1<em>$2</em>"
  );
  // Imagens: ![alt](url) — antes do link pra não capturar "!" como texto.
  // Permite http(s) e data:image/*;base64.
  s = s.replace(
    /!\[([^\]\n]*)\]\(((?:https?:\/\/|data:image\/[a-zA-Z+.-]+;base64,)[^\s)]+)\)/g,
    (_m, alt, url) =>
      `<img src="${url}" alt="${alt}" class="my-1 inline-block max-h-72 max-w-full rounded-md border border-border" loading="lazy" />`
  );
  s = s.replace(
    /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label, url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary underline">${label}</a>`
  );
  // Citation anchoring: [Doc N] vira um chip clicável que dispara um evento
  // 'caderno:cite' no document — listeners do caderno realçam o doc N.
  // [[project-caderno-notebooklm-roadmap]] Fase 1.
  s = s.replace(
    /\[Doc\s+(\d+)(?:,\s*par[áa]grafo\s*(\d+))?\]/gi,
    (_m, docNum: string, _para?: string) =>
      `<button type="button" data-cite-doc="${docNum}" class="ai-cite mx-0.5 inline-flex items-center gap-0.5 rounded border border-sky-500/30 bg-sky-500/10 px-1 py-0 align-baseline text-[0.85em] font-bold text-sky-700 hover:bg-sky-500/20 dark:text-sky-300">Doc ${docNum}</button>`
  );
  // Restaura math inline protegida no início (escape mínimo pro KaTeX casar).
  s = s.replace(/INL(\d+)/g, (_m, idx: string) =>
    escapeMathForHtml(inlineTokens[Number(idx)] ?? "")
  );
  return s;
}

/** Detecta a "linha separadora" de uma tabela markdown:
 *  Exemplo: `| --- | :--- | ---: |` (com `:` opcional pra alinhamento).
 *  Cada célula precisa ter pelo menos um hífen. */
function isTableSeparator(line: string): { aligns: Array<"left" | "right" | "center" | "default"> } | null {
  if (!line.includes("|")) return null;
  // Tira pipes das pontas pra não criar células fantasma.
  const inner = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  const cells = inner.split("|").map((c) => c.trim());
  if (cells.length === 0) return null;
  const aligns: Array<"left" | "right" | "center" | "default"> = [];
  for (const c of cells) {
    if (!/^:?-{1,}:?$/.test(c)) return null; // não é uma célula separadora válida
    const left = c.startsWith(":");
    const right = c.endsWith(":");
    aligns.push(left && right ? "center" : right ? "right" : left ? "left" : "default");
  }
  return { aligns };
}

function splitTableRow(line: string): string[] {
  const inner = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return inner.split("|").map((c) => c.trim());
}

function renderTable(
  headerCells: string[],
  separator: { aligns: Array<"left" | "right" | "center" | "default"> },
  bodyRows: string[][]
): string {
  function alignClass(i: number): string {
    const a = separator.aligns[i] ?? "default";
    if (a === "right") return "text-right";
    if (a === "center") return "text-center";
    return "text-left";
  }
  const thead =
    "<thead class=\"bg-muted/30\"><tr>" +
    headerCells
      .map(
        (c, i) =>
          `<th class="${alignClass(i)} border border-border px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider">${renderInlineForAi(c)}</th>`
      )
      .join("") +
    "</tr></thead>";
  const tbody =
    "<tbody>" +
    bodyRows
      .map(
        (row) =>
          "<tr>" +
          row
            .map(
              (c, i) =>
                `<td class="${alignClass(i)} border border-border px-2 py-1.5 align-top">${renderInlineForAi(c)}</td>`
            )
            .join("") +
          "</tr>"
      )
      .join("") +
    "</tbody>";
  return `<div class="my-3 overflow-x-auto rounded-md border border-border"><table class="w-full border-collapse text-xs">${thead}${tbody}</table></div>`;
}

/** Converte markdown leve em HTML. Fluxo por linha, mantendo a regra
 *  de blocos do GFM (cabeçalho ## …, listas - / * / 1., parágrafos, tabelas). */
export function renderAiMarkdown(raw: string): string {
  // Protege DISPLAY math (`$$...$$` e `\[...\]`) ANTES do split por linha. Sem
  // isso, um bloco `$$\n...\n$$` vira <p>$$<br>...<br>$$</p> e o KaTeX
  // auto-render não casa os delimitadores separados por <br>. Extraímos pra um
  // placeholder ASCII inerte e restauramos no fim. [[project-markdown-math-protect]]
  const SOH = String.fromCharCode(1);
  const mathBlocks: string[] = [];
  const protectedRaw = raw
    .replace(/\$\$[\s\S]+?\$\$/g, (m) => {
      mathBlocks.push(m);
      return `${SOH}BLK${mathBlocks.length - 1}${SOH}`;
    })
    .replace(/\\\[[\s\S]+?\\\]/g, (m) => {
      mathBlocks.push(m);
      return `${SOH}BLK${mathBlocks.length - 1}${SOH}`;
    });
  const lines = protectedRaw.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  type ListKind = "ul" | "ol";
  const listStack: ListKind[] = [];

  function closeListsTo(depth: number) {
    while (listStack.length > depth) {
      const kind = listStack.pop()!;
      out.push(kind === "ul" ? "</ul>" : "</ol>");
    }
  }

  let paragraphBuffer: string[] = [];
  function flushParagraph() {
    if (paragraphBuffer.length === 0) return;
    const inner = paragraphBuffer.map(renderInlineForAi).join("<br>");
    out.push(`<p class="my-2 leading-relaxed">${inner}</p>`);
    paragraphBuffer = [];
  }

  let alignStack: Array<"left" | "center" | "right"> = [];
  function openAlign(a: "left" | "center" | "right") {
    alignStack.push(a);
    const cls = a === "center" ? "text-center" : a === "right" ? "text-right" : "text-left";
    out.push(`<div class="my-1 ${cls}">`);
  }
  function closeAlign() {
    if (alignStack.length === 0) return;
    alignStack.pop();
    out.push("</div>");
  }

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const line = rawLine.replace(/\s+$/g, "");
    if (line.trim() === "") {
      flushParagraph();
      // Listas "soltas" (loose): uma linha em branco ENTRE itens não deve
      // fechar a lista — senão o próximo item abre um <ol> novo e a numeração
      // (que vem do CSS list-decimal, por posição) reinicia em 1. Espia a
      // próxima linha com conteúdo: se ainda for item de lista, mantém a lista
      // aberta (a lógica de profundidade do próximo item ajusta o stack). Só
      // fecha de verdade quando o que vem depois não é item. Bug 2026-05-29:
      // "1. … 1. …" em vez de "1. … 2. …". Semântica CommonMark.
      if (listStack.length > 0) {
        let k = i + 1;
        while (k < lines.length && lines[k].trim() === "") k++;
        if (k < lines.length && /^(\s*)([-*]|\d+\.)\s+(.+)$/.test(lines[k])) {
          continue;
        }
      }
      closeListsTo(0);
      continue;
    }

    // Bloco de alinhamento: `::: left|center|right` … `:::`
    const alignOpen = /^:::\s*(left|center|right)\s*$/.exec(line);
    if (alignOpen) {
      flushParagraph();
      closeListsTo(0);
      openAlign(alignOpen[1] as "left" | "center" | "right");
      continue;
    }
    if (line.trim() === ":::") {
      flushParagraph();
      closeListsTo(0);
      closeAlign();
      continue;
    }

    // Régua horizontal: --- ou *** ou ___ sozinho na linha.
    if (/^(?:-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushParagraph();
      closeListsTo(0);
      out.push('<hr class="my-3 border-border" />');
      continue;
    }

    // Code block fenced: ``` opcionalLang … ```
    const fenceMatch = /^```\s*([a-zA-Z0-9_-]*)\s*$/.exec(line);
    if (fenceMatch) {
      flushParagraph();
      closeListsTo(0);
      const lang = fenceMatch[1];
      const codeLines: string[] = [];
      let j = i + 1;
      while (j < lines.length && !/^```\s*$/.test(lines[j].trim())) {
        codeLines.push(lines[j]);
        j++;
      }
      const escaped = escapeHtml(codeLines.join("\n"));
      const langAttr = lang ? ` data-lang="${escapeHtml(lang)}"` : "";
      out.push(
        `<pre class="my-2 overflow-x-auto rounded-md border border-border bg-muted/40 p-3 text-xs"${langAttr}><code>${escaped}</code></pre>`
      );
      i = j;
      continue;
    }

    // Tabela GFM: linha com pipes seguida de linha-separadora.
    // Lookahead: precisa ter próxima linha e ela ser uma separadora válida.
    if (line.includes("|") && i + 1 < lines.length) {
      const sep = isTableSeparator(lines[i + 1]);
      if (sep) {
        flushParagraph();
        closeListsTo(0);
        const headerCells = splitTableRow(line);
        const bodyRows: string[][] = [];
        let j = i + 2;
        while (j < lines.length) {
          const next = lines[j];
          if (next.trim() === "" || !next.includes("|")) break;
          bodyRows.push(splitTableRow(next));
          j++;
        }
        out.push(renderTable(headerCells, sep, bodyRows));
        i = j - 1;
        continue;
      }
    }

    // Header: # / ## / ### (1-3) — todos em violeta pra destacar das
    // respostas longas da IA. H1 mais forte, H3 mais leve em tamanho mas
    // mantendo a cor pra reconhecimento visual rápido.
    const hMatch = /^(#{1,3})\s+(.+)$/.exec(line);
    if (hMatch) {
      flushParagraph();
      closeListsTo(0);
      const level = hMatch[1].length;
      const text = renderInlineForAi(hMatch[2]);
      const cls =
        level === 1
          ? "mt-4 mb-2 text-base font-bold text-violet-700 dark:text-violet-300"
          : level === 2
            ? "mt-4 mb-2 text-sm font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300"
            : "mt-3 mb-1 text-xs font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400";
      out.push(`<h${level} class="${cls}">${text}</h${level}>`);
      continue;
    }

    // Lista — detecta indentação (2 espaços = 1 nível).
    const liMatch = /^(\s*)([-*]|\d+\.)\s+(.+)$/.exec(line);
    if (liMatch) {
      flushParagraph();
      const indent = liMatch[1].replace(/\t/g, "  ").length;
      const depth = Math.floor(indent / 2) + 1;
      const isOrdered = /^\d+\.$/.test(liMatch[2]);
      const kind: ListKind = isOrdered ? "ol" : "ul";
      const content = renderInlineForAi(liMatch[3]);
      // Ajusta stack pra essa profundidade.
      if (depth > listStack.length) {
        // Abre listas novas até bater na profundidade.
        while (listStack.length < depth) {
          const k = listStack.length === depth - 1 ? kind : "ul";
          listStack.push(k);
          out.push(
            k === "ul"
              ? '<ul class="my-2 ml-5 list-disc space-y-1">'
              : '<ol class="my-2 ml-5 list-decimal space-y-1">'
          );
        }
      } else if (depth < listStack.length) {
        closeListsTo(depth);
      } else {
        // Mesma profundidade — fecha+abre se mudou a kind do topo.
        const top = listStack[listStack.length - 1];
        if (top !== kind) {
          closeListsTo(depth - 1);
          listStack.push(kind);
          out.push(
            kind === "ul"
              ? '<ul class="my-2 ml-5 list-disc space-y-1">'
              : '<ol class="my-2 ml-5 list-decimal space-y-1">'
          );
        }
      }
      out.push(`<li>${content}</li>`);
      continue;
    }

    // Linha de parágrafo — acumula.
    closeListsTo(0);
    paragraphBuffer.push(line);
  }
  flushParagraph();
  closeListsTo(0);
  while (alignStack.length > 0) closeAlign();
  // Restaura os blocos display protegidos, intactos num único nó de texto, pro
  // KaTeX auto-render casar `$$...$$` / `\[...\]`.
  return out
    .join("")
    .replace(new RegExp(`${SOH}?BLK(\\d+)${SOH}?`, "g"), (_m, idx: string) =>
      escapeMathForHtml(mathBlocks[Number(idx)] ?? "")
    );
}

/** Bloco visual que renderiza markdown + math da IA, mais botão Copiar.
 *  Conteúdo é estático (não conversacional); usuário pode copiar o texto
 *  bruto pra colar em outra ferramenta. */
export function AiMarkdownBlock({
  text,
  className
}: {
  text: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void ensureKaTeX().then(() => renderMathIn(ref.current));
  }, [text]);

  // Event delegation pra citation anchoring: clica em [Doc N] →
  // dispara um CustomEvent global ('caderno:cite') que componentes do caderno
  // (DocsPanel, viewer) podem ouvir e destacar o documento alvo.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const btn = target.closest<HTMLButtonElement>("[data-cite-doc]");
      if (!btn) return;
      const n = Number(btn.getAttribute("data-cite-doc"));
      if (!Number.isFinite(n) || n < 1) return;
      e.preventDefault();
      document.dispatchEvent(
        new CustomEvent("caderno:cite", { detail: { docIndex: n - 1 } })
      );
    }
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, []);

  const html = renderAiMarkdown(text || "");

  return (
    <div
      ref={ref}
      class={`ai-md text-sm leading-relaxed text-foreground/90 ${className ?? ""}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** Botão Copiar reutilizável. Mostra feedback "Copiado!" por 1.5s. */
export function CopyAiTextButton({
  text,
  size = "sm",
  className
}: {
  text: string;
  size?: "xs" | "sm";
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore (browser sem permissão / iframe / etc)
    }
  }

  const padCls = size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]";
  const iconSize = size === "xs" ? "size-2.5" : "size-3";

  return (
    <button
      type="button"
      onClick={copy}
      title="Copiar texto"
      class={`inline-flex items-center gap-1 rounded-md border border-border bg-background font-semibold transition-colors hover:bg-accent disabled:opacity-50 ${padCls} ${className ?? ""}`}
    >
      {copied ? (
        <>
          <Check className={`${iconSize} text-emerald-600`} />
          Copiado!
        </>
      ) : (
        <>
          <Copy className={iconSize} />
          Copiar
        </>
      )}
    </button>
  );
}
